/**
 * NZ IRD PAYE calculation (tax + ACC earners' levy).
 * Uses rates from 1 April 2021 / 31 July 2024 PAYE deduction tables.
 * Reference: https://www.ird.govt.nz/income-tax/income-tax-for-individuals/tax-codes-and-tax-rates-for-individuals/tax-rates-for-individuals
 */

/** Pay frequency for annualising income (primary codes) */
export type PayFrequency = 'weekly' | 'fortnightly' | 'monthly';

/** NZ PAYE brackets (combined tax + ACC). Annual thresholds; rate applied to marginal income. */
const PAYE_BRACKETS_ANNUAL: { threshold: number; rate: number }[] = [
  { threshold: 15_600, rate: 0.1217 },
  { threshold: 53_500, rate: 0.1917 },
  { threshold: 78_100, rate: 0.3167 },
  { threshold: 152_790, rate: 0.3467 },
  { threshold: 180_000, rate: 0.33 },
  { threshold: Infinity, rate: 0.39 },
];

/** Secondary tax code → flat PAYE rate (no annualisation). */
const SECONDARY_RATES: Record<string, number> = {
  SB: 0.1217,
  S: 0.1917,
  SH: 0.3167,
  ST: 0.3467,
  SA: 0.39,
  'SB SL': 0.1217,
  'S SL': 0.1917,
  'SH SL': 0.3167,
  'ST SL': 0.3467,
  'SA SL': 0.39,
};

function periodsPerYear(frequency: PayFrequency): number {
  switch (frequency) {
    case 'weekly': return 52;
    case 'fortnightly': return 26;
    case 'monthly': return 12;
    default: return 26;
  }
}

/** Primary tax codes use full marginal brackets (annualised). */
function isPrimaryCode(code: string): boolean {
  const c = (code || '').trim().toUpperCase();
  return c === 'M' || c === 'ME' || c === 'M SL' || c === 'ME SL';
}

/**
 * Compute PAYE for a single pay period.
 * @param grossForPeriod - Gross pay for the period (e.g. one week/fortnight/month).
 * @param taxCode - IRD tax code (e.g. M, ME, S, SH, ST, SA, SB; SL variants supported).
 * @param payFrequencyOrDays - Pay frequency for annualising, or (when number) actual days in period for proportional calculation.
 * @returns PAYE amount (tax + ACC) to deduct for the period.
 */
export function calculatePaye(
  grossForPeriod: number,
  taxCode: string,
  payFrequencyOrDays: PayFrequency | number = 'fortnightly'
): number {
  if (grossForPeriod <= 0) return 0;

  const code = (taxCode || 'M').trim().toUpperCase().replace(/\s+/g, ' ');

  // Secondary: flat rate on this period's gross
  const secondaryRate = SECONDARY_RATES[code];
  if (secondaryRate != null) {
    return Math.round(grossForPeriod * secondaryRate * 100) / 100;
  }

  // Primary (M, ME, M SL, ME SL): annualise, apply marginal brackets, then prorate back
  const useProportional = typeof payFrequencyOrDays === 'number' && payFrequencyOrDays > 0;
  const periods = useProportional
    ? 365 / payFrequencyOrDays
    : periodsPerYear(payFrequencyOrDays as PayFrequency);
  const annualGross = grossForPeriod * periods;
  let annualTax = 0;
  let prevThreshold = 0;

  for (const { threshold, rate } of PAYE_BRACKETS_ANNUAL) {
    if (annualGross <= prevThreshold) break;
    const band = Math.min(threshold - prevThreshold, annualGross - prevThreshold);
    annualTax += band * rate;
    prevThreshold = threshold;
    if (annualGross <= threshold) break;
  }

  const payeForPeriod = annualTax / periods;
  return Math.round(payeForPeriod * 100) / 100;
}

/**
 * Infer pay frequency from number of days in the pay period (for display/calculation).
 */
export function inferPayFrequency(daysInPeriod: number): PayFrequency {
  if (daysInPeriod <= 8) return 'weekly';
  if (daysInPeriod <= 18) return 'fortnightly';
  return 'monthly';
}
